import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";

type URLSearchParamsInit = ConstructorParameters<typeof URLSearchParams>[0];

interface NavigateOptions {
  replaceState?: boolean;
}

type SetHashSearchParams = (
  nextInit?:
    | URLSearchParamsInit
    | ((prev: URLSearchParams) => URLSearchParamsInit),
  options?: NavigateOptions,
) => void;

/**
 * Hook for using the URL hash as a query string. This can be useful if you want to persist state in the URL, but not
 * make a round trip to the server when they change (e.g. if they don't have impact on what data is loaded). This is
 * more ergonomic when trying to support offline mode.
 */
export const useHashParams = (
  options?: NavigateOptions,
): readonly [URLSearchParams, SetHashSearchParams] => {
  const { hash: raw, search, state: locationState } = useLocation();
  const navigate = useNavigate();

  const params = useMemo(
    () => new URLSearchParams(raw.replace(/^#/, "").trim()),
    [raw],
  );

  const setParams = useCallback<SetHashSearchParams>(
    (init, passedOptions) => {
      const oldHash = raw.replace(/^#/, "").trim();
      let newParams: URLSearchParams;
      if (typeof init === "function") {
        newParams = new URLSearchParams(init(params));
      } else {
        newParams = new URLSearchParams(init);
      }
      const hash = newParams.toString();
      if (hash !== oldHash) {
        navigate(
          {
            search,
            hash,
          },
          {
            replace: passedOptions?.replaceState || options?.replaceState,
            state: locationState,
          },
        );
      }
    },
    [raw, params, navigate, search, options?.replaceState, locationState],
  );

  return [params, setParams] as const;
};

export const useHashParam = (key: string, options?: NavigateOptions) => {
  const [params, setParams] = useHashParams(options);
  const param = useMemo(() => {
    return [...new Set(params.getAll(key) ?? [])];
  }, [params, key]);

  const setParam = useCallback(
    (
      nextState: string[] | ((prevState: string[]) => string[]),
      passedOptions?: NavigateOptions,
    ) =>
      setParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete(key);

        const newValues =
          typeof nextState === "function"
            ? nextState(prev.getAll(key))
            : nextState;

        newValues.forEach((value) => newParams.append(key, value));

        return newParams;
      }, passedOptions),
    [key, setParams],
  );

  return [param, setParam] as const;
};
